// ParticipantsSelector.tsx
import React, { useEffect, useState } from "react";
import { Avatar, Checkbox, Grid, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import scStyled from 'styled-components';
import { ReservationsParticipant, Reservation } from "src/types";
import { linkAvatar } from "@utils";
import default_avatar from "@assets/Images/default_avatar.jpg";
import { PassChangeButton } from "../Profile";
import { AppCustomeSelect, AppInput } from "../Common";
import { IUserInfo } from "@interfaces";
import { selectAuth, selectLists, useAppSelector } from "@redux";

const defaultParticipantGroup = {
  value: 0,
  label: '全てのグループ',
};

type ParticipantListProps = {
  customHeight?: string;
};

export const ParticipantsSelector = ({
  currentUserId,
  reservationId,
  originalReservation,
  users,
  groups,
  groupsUsers,
  selectedUsers,
  setSelectedUsers,
  showCheckbox = false,
  canSelectSelf = true,
  userSelectionType = "default",
  leftListHeight, 
  rightListHeight,
} : {
  currentUserId: number,
  reservationId?: number | null, // null means we're making a new reservation (thus no ID yet), undefined means we're doing something unrelated to a reservation
  originalReservation?: Reservation | null,
  users: IUserInfo[],
  groups: {id: number, name: string}[],
  groupsUsers: {user_id: number, group_id: number}[],
  selectedUsers: ReservationsParticipant[],
  setSelectedUsers: React.Dispatch<React.SetStateAction<ReservationsParticipant[]>>,
  showCheckbox?: boolean,
  canSelectSelf?: boolean,
  userSelectionType?: "favorite" | "default" | null
  leftListHeight?: string,
  rightListHeight?: string,
}) => {

  // Participants state
  const [availableUsers, setAvailableUsers] = useState<ReservationsParticipant[]>([]);
  const [selectedCurrentUserIds, setSelectedCurrentUserIds] = useState<number[]>([]);
  const [selectedAvailableUserIds, setSelectedAvailableUsers] = useState<number[]>([]);
  
  const [participantGroup, setParticipantGroup] = useState<{value: number, label: string}>(defaultParticipantGroup);
  const participantGroupOptions: {value: number, label: string}[] = groups.map(group => ({
    value: group.id,
    label: group.name,
  }));
  participantGroupOptions.unshift(defaultParticipantGroup);

  const groupParticipants = availableUsers.filter(user => {
    if (participantGroup === null || participantGroup.value === 0) {
      return true; // Show all users if no group is selected
    }
    return groupsUsers.some(gu => gu.user_id === user.user.id && gu.group_id === participantGroup.value);
  });

  // Get user ID from auth state
  const user = useAppSelector(selectAuth);
  const userId = user.userInfo?.id;

  // Get lists (favorites and default participants) from slice
  const lists = useAppSelector(selectLists).data.filter(list => !list.deleted_at && list.creator_id === userId);
  const defaultParticipantIds = [
    ...(userId ? [userId] : []),
    ...(lists.find(list => list.type === userSelectionType && !list.deleted_at)?.participants || [])
      .filter(id => id !== userId)
  ];

  const [searchString, setSearchString] = useState<string>("");

  const filteredParticipants = groupParticipants.filter(participant => {
    if (searchString === "") {
      return true; // Show all users if no search string is provided
    }
    const fullName = `${participant.user.last_name} ${participant.user.first_name}`;
    return fullName.toLowerCase().includes(searchString.toLowerCase());
  });

  showCheckbox = false; // Disable checkbox for now on all forms, even though this means we can't set required participants
  //showCheckbox = showCheckbox && reservationId !== null; // Only show checkbox if we're editing an existing reservation

  useEffect(() => {
    if (users) {
      let initialSelectedUsers: ReservationsParticipant[] = [];
      // If reservationId is undefined, we are not editing a reservation
      if (reservationId === undefined) {
        initialSelectedUsers = selectedUsers;
      }

      // If this is a new reservation (no id) and not editing an existing one, we need to load default participants
      if (reservationId === null) {
        // Find default participants in the users list
        initialSelectedUsers = users
          .filter(user => defaultParticipantIds.includes(user.id || 0))
          .map(user => ({
            required: true, // Set as required by default
            user: user
        }))
        .sort((a, b) => {
          if (a.user.id === userId) return -1;
          if (b.user.id === userId) return 1;
          return (a.user.id || 0) - (b.user.id || 0);
        });
      }

      // If this is an existing reservation, populate selected users
      if (reservationId && originalReservation) {
        initialSelectedUsers = originalReservation?.participants || [];
        initialSelectedUsers = [...initialSelectedUsers].sort((a, b) => {
          if (a.user.id === userId) return -1;
          if (b.user.id === userId) return 1;
          return (a.user.id || 0) - (b.user.id || 0);
        });
      }

      setSelectedUsers(initialSelectedUsers);

      // Create a Set of participant user IDs to exclude from available users
      const initialSelectedIds = new Set(
        initialSelectedUsers.map(p => p.user.id || 0)
      );
      
      // Filter users that aren't already selected as participants
      const initialAvailableUsers = users
        .filter(user => !initialSelectedIds.has(user.id || 0))
        .filter(user => canSelectSelf || user.id !== currentUserId) // Exclude current user if not allowed
        .map(user => ({
          required: false,
          user: user
        }))
        .sort((a, b) => {
          if (a.user.id === userId) return -1;
          if (b.user.id === userId) return 1;
          return (a.user.id || 0) - (b.user.id || 0);
        });

      setAvailableUsers(initialAvailableUsers);
    }
  }, [users, originalReservation, reservationId]);

  // When a new list of favorites is made, update available users to show all users
  useEffect(() => {
    if (users && selectedUsers) {
      // Create a Set of participant user IDs to exclude from available users
      const selectedIds = new Set(
        selectedUsers.map(p => p.user?.id || 0)
      );
      
      // Filter users that aren't already selected as participants
      const newAvailableUsers = users
        .filter(user => !selectedIds.has(user.id || 0))
        .filter(user => canSelectSelf || user.id !== currentUserId)
        .map(user => ({
          required: false,
          user: user
        }))
        .sort((a, b) => {
          if (a.user.id === userId) return -1;
          if (b.user.id === userId) return 1;
          return (a.user.id || 0) - (b.user.id || 0);
        });

      setAvailableUsers(newAvailableUsers);
    }
  }, [users, selectedUsers, canSelectSelf, currentUserId]);

  // Handle adding participants
  const handleAddParticipants = () => {
    const newParticipants = [...selectedUsers];
    const newAvailable = [...availableUsers];
    
    // Add selected available users to participants
    selectedAvailableUserIds.forEach(userId => {
      const userIndex = newAvailable.findIndex(participant => participant.user.id === userId);
      if (userIndex !== -1) {
        const user = newAvailable[userIndex];
        newParticipants.push({ ...user, required: false });
        newAvailable.splice(userIndex, 1);
      }
    });

    newParticipants.sort((a, b) => {
      if (a.user.id === userId) return -1;
      if (b.user.id === userId) return 1;
      return (a.user.id || 0) - (b.user.id || 0);
    });
    newAvailable.sort((a, b) => {
      if (a.user.id === userId) return -1;
      if (b.user.id === userId) return 1;
      return (a.user.id || 0) - (b.user.id || 0);
    });

    setSelectedUsers(newParticipants);
    setAvailableUsers(newAvailable);
    setSelectedAvailableUsers([]);
  };

  // Handle removing participants
  const handleRemoveParticipants = () => {
    const newParticipants = [...selectedUsers];
    const newAvailable = [...availableUsers];

    // Add selected participants back to available users
    selectedCurrentUserIds.forEach(userId => {
      const userIndex = newParticipants.findIndex(participant => participant.user.id === userId);
      if (userIndex !== -1) {
        const user = newParticipants[userIndex];
        newAvailable.push({ ...user });
        newParticipants.splice(userIndex, 1);
      }
    });

    newParticipants.sort((a, b) => {
      if (a.user.id === userId) return -1;
      if (b.user.id === userId) return 1;
      return (a.user.id || 0) - (b.user.id || 0);
    });
    newAvailable.sort((a, b) => {
      if (a.user.id === userId) return -1;
      if (b.user.id === userId) return 1;
      return (a.user.id || 0) - (b.user.id || 0);
    });

    setSelectedUsers(newParticipants);
    setAvailableUsers(newAvailable);
    setSelectedCurrentUserIds([]);
  };

  // Handle required checkbox change
  const handleRequiredChange = (userId: number) => {
    setSelectedUsers(prevParticipants => 
      prevParticipants.map(participant => 
        participant.user.id === userId 
          ? { ...participant, required: !participant.required }
          : participant
      )
    );
  };
  

  return (
    <Grid item={true} xs={12} sx={{ marginTop: '2rem'}}>
      <Grid item={true} xs={6}>
      <div
      style={{
        display:"flex",
        flexDirection:"row",
        justifyContent:"space-between"
      }}>
      <StyledLabel>
        参加者
      </StyledLabel>
      <StyledLabel>
        <span style={{ fontSize:"12px", marginRight:"7px", display: showCheckbox ? undefined : "none" }}>必須</span>
      </StyledLabel>
      </div>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={6}>
          {/* Left side */}
          <ParticipantList
            customHeight={leftListHeight} 
          >
            {selectedUsers.map((participant) => {
              if (!participant.user) {
                console.log("Left List - Participant user is null or undefined:", participant);
                return null;
              }
              // debugging avatar
//              console.log("Left list - participant:", JSON.stringify(participant, null, 2));
//              console.log("Left list - participant User Avatar:", participant?.user?.avatar);
//              console.log("Left list - avatar type:", typeof participant?.user?.avatar);
              const avatarPath = typeof participant.user.avatar === 'string'
                                ? participant.user.avatar
                                : participant.user.avatar?.path; // Added optional chaining for path
//              console.log("Left list - calculated Avatar Path:", avatarPath);
//              console.log("Left list - linkAvatar output:", participant?.user?.avatar ? linkAvatar(avatarPath) : "no avatar, using default");
              return (
                <ListItem
                  key={participant.user.id}
                  secondaryAction={
                    <Checkbox
                      style={{display: showCheckbox ? undefined : 'none'}}
                      edge="start"
                      title="参加必須"
                      checked={!!participant.required}
                      onChange={() => handleRequiredChange(participant.user.id || 0)}
                      sx={{
                        padding: 0,
                        marginRight: '5px',
                        cursor:'pointer',
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  onClick={() => {
                    if (selectedCurrentUserIds.includes(participant.user.id || 0)) {
                      setSelectedCurrentUserIds(selectedCurrentUserIds.filter(id => id !== participant.user.id));
                    } else {
                      const newIds = [...selectedCurrentUserIds, participant.user.id || 0];
                      // Sort so current user appears first
                      newIds.sort((a, b) => {
                        if (a === userId) return -1;
                        if (b === userId) return 1;
                        return a - b;
                      });
                      setSelectedCurrentUserIds(newIds);
                    }
                  }}
                  disableGutters
                  divider
                  sx={{
                    bgcolor: selectedCurrentUserIds.includes(participant.user.id || 0) ? '#FFCEAF' : 'transparent',
                    pl: 1, cursor:'pointer',
                    overflow: 'hidden',
                  }}
                >
                  <ListItemIcon
                  sx={{
                    minWidth: 'auto', 
                    mr: 1,  
                  }}
                  >
                    <Avatar
                      className="image"
                      src={
                        participant?.user?.avatar?.path
                          ? linkAvatar(participant.user?.avatar?.path)
                          : default_avatar
                      }
                      sx={{ width: 24, height: 24 }} 
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${participant.user.last_name} ${participant.user.first_name} ${participant.user.id === currentUserId ? '[自分]' : ''}`}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: '13px !important',
                          fontWeight: participant.user.id === currentUserId ? 'bold' : 'normal'
                        },
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </ParticipantList>
          <PassChangeButton
            style={{width:'100%', marginTop:'1rem'}}
            onClick={handleRemoveParticipants}
            disabled={selectedCurrentUserIds.length === 0}
          >メンバーを削除 →</PassChangeButton>
        </Grid>

        <Grid item xs={6} sm={6}>
          <AppCustomeSelect
            name="participant_group"
            value={participantGroup}
            onChange={(values) => {setParticipantGroup(values); }}
            options={participantGroupOptions}
            isSearchable={false}
            style={{
                  fontSize:"13px"
                }}
          />
          <AppInput
            placeholder="検索"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            style={{margin:'1rem 0'}}
          />

          {/* Right side */}
          <ParticipantList
          customHeight={rightListHeight}
          >
            {filteredParticipants.map((participant) => {
              if (!participant.user) return null;
              return (
                <ListItem
                  key={participant.user.id}
                  onClick={() => {
                    if (selectedAvailableUserIds.includes(participant.user.id || 0)) {
                      setSelectedAvailableUsers(selectedAvailableUserIds.filter(id => id !== participant.user.id));
                    } else {
                      const newIds = [...selectedAvailableUserIds, participant.user.id || 0];
                      // Sort so current user appears first
                      newIds.sort((a, b) => {
                        if (a === userId) return -1;
                        if (b === userId) return 1;
                        return a - b;
                      });
                      setSelectedAvailableUsers(newIds);
                    }
                  }}
                  disableGutters
                  divider
                  sx={{
                    bgcolor: selectedAvailableUserIds.includes(participant.user.id || 0) ? '#FFCEAF' : 'transparent',
                    pl: 1,cursor:'pointer',
                    overflow: 'hidden',
                  }}
                >
                  <ListItemIcon
                  sx={{
                    minWidth: 'auto', 
                    mr: 1,  
                  }}
                  >
                    <Avatar
                      className="image"
                      src={
                        participant?.user?.avatar?.path
                          ? linkAvatar(participant.user?.avatar?.path)
                          : default_avatar
                      }
                      sx={{ width: 24, height: 24 }} 
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${participant.user.last_name} ${participant.user.first_name}`}
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: '13px !important',
                        },
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </ParticipantList>
          <PassChangeButton
            style={{width:'100%', marginTop:'1rem'}}
            onClick={handleAddParticipants}
            disabled={selectedAvailableUserIds.length === 0}
          >← メンバーを追加</PassChangeButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

const StyledLabel = scStyled.div`
  font-size: 14px;
  margin-bottom:3px;
`;

const ParticipantList = scStyled(List)<ParticipantListProps>`
  overflow-y: auto;
  border: 1px solid lightgray;
  border-radius: 10px;
  background-color:${(p) => p.theme.colors.white02};
  padding-top:0 !important;
  &::-webkit-scrollbar {
    width: 11px;
  }
    &::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.04);
  }

  ${(p) => p.customHeight ? `height: ${p.customHeight};` : `
    height: 300px; 
    @media (min-width: 768px) {
      height: 250px; 
    }
  `}
`;
